import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { debounce } from "lodash";
import { Alert, Button, Chip, Dialog, FormControl, InputLabel, MenuItem, Paper, Select, Skeleton, Snackbar, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, TextField, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Add } from "@mui/icons-material";
import { DateRangePicker } from "rsuite";
import { Calendar } from "react-feather";
import { authData, withAdminAuth } from "../../../components/GetAuth";
import { formatGBP2 } from "../../../components/Features";
import PageHeader from "../../../components/PageHeader";
import TablePaginationComponent from "../../../components/TablePagination";
import { StyledMainTableCell, StyledMainTableRow } from "../../../components/TableComponent";
import { useWebsite } from "../../../../context/WebsiteContext";
import { useSiteBranding } from "../../../../hooks/useSiteBranding";
import styles from "../shop.module.css";
import OrderDetailsPopup from "./OrderDetailsPopup";
import { OrderCardsLoader, OrderRowsLoader } from "./OrderListLoader";
import { ORDER_PROGRESS, PAYMENT_METHOD_LABEL, progressColor } from "./orderStatus";

const SUMMARY = [
  { key: "placed", label: "New" },
  { key: "confirmed", label: "Confirmed" },
  { key: "out", label: "Out For Delivery" },
  { key: "delivered", label: "Delivered" },
  { key: "cancelled", label: "Cancelled" },
];

// Shop Now orders placed by customers; each one has a local delivery booking for the driver
const ShopOrderList = () => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const isMobile = useMediaQuery("(max-width:768px)");
  const navigate = useNavigate();
  const canCreate = auth?.user_type === 2 || auth?.permissions?.indexOf("shop:orders") > -1;
  const { websiteData } = useWebsite();
  useSiteBranding("Shop orders", websiteData);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState({ search: "", status: "all", payment: "", source: "", date: null });
  const [viewId, setViewId] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  // The status counts only show placeholders before the first load
  const [loaded, setLoaded] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", type: "success" });

  const debouncedSearch = useMemo(() => debounce((value) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setPage(0);
  }, 500), []);

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    setLoading(true);
    axios.get(process.env.REACT_APP_API_URL + "v1/shop/orders", {
      headers: { Authorization: `Bearer ${API_TOKEN}` },
      params: {
        page: page + 1,
        per_page: rowsPerPage,
        search: filters.search,
        status: filters.status,
        payment: filters.payment,
        source: filters.source,
        start_date: filters.date ? dayjs(filters.date[0]).format("YYYY-MM-DD") : null,
        end_date: filters.date ? dayjs(filters.date[1]).format("YYYY-MM-DD") : null,
      },
    }).then((response) => {
      if (response.data.status === "SUCCESS") {
        setRows(response.data.data.data || []);
        setTotal(response.data.data.total || 0);
        setCounts(response.data.data.status_count || {});
      }
      setLoading(false);
      setLoaded(true);
    }).catch(() => { setLoading(false); setLoaded(true); });
  }, [API_TOKEN, page, rowsPerPage, filters, refresh]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const notify = (message, type) => setSnackbar({ open: true, message, type });

  // Reload so the row and the status counts match the change
  const onUpdated = () => setRefresh((r) => r + 1);

  const progressChip = (row) => <Chip size="small" label={row.progress} className={`${styles.ProgressChip}`} style={{ background: progressColor(row.progress) }} />;
  const paymentChip = (row) => (row.progress === "Cancelled" ? "-" : row.payment_status === 1 ? <Chip size="small" color="success" label="Paid" /> : <Chip size="small" color="error" label="Unpaid" />);
  // Online = the customer ordered in Shop Now; otherwise our team booked it for them
  const placedBy = (row) => (row.source === "admin" ? <>Our team<span className={`${styles.SubText}`}>{row.added_by?.name || ""}</span></> : "Customer (online)");

  return (<>
    <div className={`${styles.MainDiv}`}>
      <PageHeader title="Orders" trail={[{ label: "Shop" }, { label: "Orders" }]} />
      <div className={`${styles.MainCard}`}>
        {canCreate && <div className={`${styles.HeadActions}`}>
          <Button className={`${styles.NewOrderBU}`} onClick={() => navigate("/booking/add-delivery-booking")}><Add /> New Order</Button>
        </div>}
        <div className={`${styles.SummaryGrid}`}>
          {SUMMARY.map((s) => (<button type="button" key={s.key} className={`${styles.SummaryCard} ${filters.status === s.key ? styles.SummaryCardActive : ""}`} onClick={() => setFilter("status", filters.status === s.key ? "all" : s.key)}>
            <h2>{loaded ? (counts[s.key] || 0) : <Skeleton variant="text" width={36} />}</h2>
            <p>{s.label}</p>
          </button>))}
        </div>

        <div className={`${styles.FilterBar}`}>
          <div className={`${styles.SearchBox}`}>
            <TextField fullWidth size="small" placeholder="Search order no or customer" value={searchInput} onChange={(e) => { setSearchInput(e.target.value); debouncedSearch(e.target.value); }} />
          </div>
          <div className={`${styles.FilterItem}`}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={filters.status} onChange={(e) => setFilter("status", e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                {Object.keys(ORDER_PROGRESS).map((label) => <MenuItem key={label} value={ORDER_PROGRESS[label].filter}>{label}</MenuItem>)}
              </Select>
            </FormControl>
          </div>
          <div className={`${styles.FilterItem}`}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment</InputLabel>
              <Select label="Payment" value={filters.payment} onChange={(e) => setFilter("payment", e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="unpaid">Unpaid</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div className={`${styles.FilterItem}`}>
            <FormControl fullWidth size="small">
              <InputLabel>Placed by</InputLabel>
              <Select label="Placed by" value={filters.source} onChange={(e) => setFilter("source", e.target.value)}>
                <MenuItem value="">Anyone</MenuItem>
                <MenuItem value="online">Customer (online)</MenuItem>
                <MenuItem value="admin">Our team</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div className={`${styles.DateFilter}`}>
            <DateRangePicker format="dd/MM/yyyy" placement="bottomEnd" showOneCalendar block caretAs={Calendar} placeholder="Order date" value={filters.date} onChange={(value) => setFilter("date", value)} />
          </div>
        </div>

        {!isMobile && (<TableContainer component={Paper} className={`${styles.TableCard}`}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledMainTableCell>Order</StyledMainTableCell>
                <StyledMainTableCell>Customer</StyledMainTableCell>
                <StyledMainTableCell>Placed By</StyledMainTableCell>
                <StyledMainTableCell>Placed</StyledMainTableCell>
                <StyledMainTableCell>Delivery Date</StyledMainTableCell>
                <StyledMainTableCell align="right">Items</StyledMainTableCell>
                <StyledMainTableCell align="right">Total</StyledMainTableCell>
                <StyledMainTableCell>Payment</StyledMainTableCell>
                <StyledMainTableCell>Status</StyledMainTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <OrderRowsLoader rows={Math.min(rowsPerPage, 8)} />}
              {!loading && rows.length === 0 && <TableRow><TableCell colSpan={9} className={`${styles.EmptyCell}`}>No orders found.</TableCell></TableRow>}
              {!loading && rows.map((row) => (<StyledMainTableRow hover key={row.id} className={`${styles.ClickRow}`} onClick={() => setViewId(row.id)}>
                <TableCell><strong>#{row.order_no}</strong></TableCell>
                <TableCell>{row.customer?.name}<span className={`${styles.SubText}`}>{row.customer?.phone}</span></TableCell>
                <TableCell>{placedBy(row)}</TableCell>
                <TableCell>{dayjs(row.created_at).format("DD MMM YYYY")}</TableCell>
                <TableCell>{row.delivery_date ? dayjs(row.delivery_date).format("DD MMM YYYY") : "-"}</TableCell>
                <TableCell align="right">{row.items_count}</TableCell>
                <TableCell align="right">{formatGBP2(row.total)}</TableCell>
                <TableCell>{paymentChip(row)}<span className={`${styles.SubText}`}>{PAYMENT_METHOD_LABEL[row.payment_method] || row.payment_method}</span></TableCell>
                <TableCell>{progressChip(row)}</TableCell>
              </StyledMainTableRow>))}
            </TableBody>
            {total > 0 && <TableFooter>
              <TableRow>
                <TablePaginationComponent count={total} colSpan={9} page={page} rowsPerPage={rowsPerPage} handleChangePage={(e, p) => setPage(p)} handleChangeRowsPerPage={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
              </TableRow>
            </TableFooter>}
          </Table>
        </TableContainer>)}

        {isMobile && (<div className={`${styles.CardList}`}>
          {loading && <OrderCardsLoader />}
          {!loading && rows.length === 0 && <div className={`${styles.EmptyCell}`}>No orders found.</div>}
          {!loading && rows.map((row) => (<div className={`${styles.Card}`} key={row.id} onClick={() => setViewId(row.id)} role="button" tabIndex={0}>
            <div className={`${styles.CardHead}`}>
              <div><strong>#{row.order_no}</strong><span className={`${styles.SubText}`}>{row.customer?.name}</span></div>
              {progressChip(row)}
            </div>
            <div className={`${styles.CardRow}`}><span>Placed</span><strong>{dayjs(row.created_at).format("DD MMM YYYY")}</strong></div>
            <div className={`${styles.CardRow}`}><span>Total</span><strong>{formatGBP2(row.total)}</strong></div>
            <div className={`${styles.CardRow}`}><span>Payment</span>{paymentChip(row)}</div>
          </div>))}
          {total > 0 && <TablePaginationComponent count={total} page={page} rowsPerPage={rowsPerPage} handleChangePage={(e, p) => setPage(p)} handleChangeRowsPerPage={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />}
        </div>)}
      </div>
    </div>

    <Dialog open={viewId > 0} onClose={() => setViewId(0)} fullWidth maxWidth="md">
      {viewId > 0 && <OrderDetailsPopup orderId={viewId} onClose={() => setViewId(0)} onUpdated={onUpdated} notify={notify} />}
    </Dialog>

    <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "top", horizontal: isMobile ? "center" : "right" }}>
      <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.type} variant="filled" sx={{ width: "100%" }}>{snackbar.message}</Alert>
    </Snackbar>
  </>);
};

export default withAdminAuth(ShopOrderList);
